import styled from '@emotion/styled';
import { invalidateCommunities } from 'apis/CompanyAPI/communities/useCommunities';
import { companiesApi, companiesAPIUrls } from 'apis/CompanyAPI/companies/companiesApi';
import { companiesKey, useCompanies } from 'apis/CompanyAPI/companies/useCompanies';
import { companyProfileByIdKey, companyProfileKey } from 'apis/CompanyAPI/companies/useCompanyProfile';
import { useCompanyLastUpdated } from 'domain/companies/profile/LastUpdated';
import { LogoHeader } from 'domain/companies/profile/header';
import { BANNER_IMAGE_ASPECT_RATIO } from 'domain/shared/Branding/BrandingForm';
import LocationInput from 'domain/shared/Location/LocationInput';
import useNotify from 'hooks/useNotify';
import { invalidate } from 'hooks/useSWR';
import useWindowWidth from 'hooks/useWindowWidth';
import React, { useRef, useState } from 'react';
import { List } from 'types/api';
import { CompanyIndustryReach, CompanyListItem, CompanyProfile, Feedback } from 'types/company';
import AiMenu from 'ui/domain/AiMenu/AiMenu';
import IndustryChip from 'ui/domain/Chips/IndustryChip';
import StageChip from 'ui/domain/Chips/StageChip';
import ChipList from 'ui/elements/Chip/ChipList';
import Button from 'ui/elements/buttons/Button';
import ButtonList from 'ui/elements/buttons/ButtonList';
import CtaButton from 'ui/elements/buttons/CtaButton';
import HorizontalDictionary from 'ui/elements/dictionary/HorizontalDictionary';
import InlineEditableField from 'ui/elements/form/InlineEditableField';
import TextField from 'ui/elements/form/TextField';
import ImageUpload from 'ui/modules/ImageUpload';
import { bluePlanetTheme } from 'ui/theme';
import { Content, DialogActions } from 'ui/views/dialogs/Dialog';
import { resize } from 'util/cloudinary';
import TruncatedList from 'util/components/TruncatedList';
import { COMPANY_REGISTRATION_NUMBER_MAX_LENGTH, MISSION_MAX_LENGTH } from 'util/constants';
import { formatShortLocation } from 'util/locationUtils';
import Resource from 'util/resource/Resource';
import useLazyResource from 'util/resource/useLazyResource';
import IndustryPicker from 'domain/labels/industries/IndustryPicker';
import CompanyAPI from 'apis/CompanyAPI';
import StagePicker from 'domain/labels/industries/StagePicker';
import Select from 'ui/elements/form/Select/Select';
import { useFundingStages } from 'apis/CompanyAPI/labels/useFundingStages';
import { getOrElse, getOrUndefined } from 'util/resource';
import AIChip from 'domain/companies/profile/content/AiChip';
import { useAiOnboardingSessionStorage } from './AIOnboarding';
import TextAvatarGroup from 'ui/elements/avatars/TextAvatarGroup';
import AvatarGroup from 'ui/elements/avatars/AvatarGroup';
import Avatar from 'ui/elements/avatars/Avatar';
import useResource from 'util/resource/useResource';
import { Snackbar } from '@mui/material';
import ThumbsDownIcon from 'ui/elements/icons/ThumbsDownIcon';
import ThumbsUpIcon from 'ui/elements/icons/ThumbsUpIcon';
import { useBreakpoints } from 'hooks/useBreakpoints';
import IconButton from 'ui/elements/icons/IconButton';

const DictionaryValue = styled.div`
  color: ${bluePlanetTheme.bluePlanetPalette.indigo.light};
`;

function BasicProfileMessage({ industryReach }: { industryReach?: CompanyIndustryReach }) {
  const [isAiOnboarding] = useAiOnboardingSessionStorage();
  const reach = industryReach?.reach.upperLimit ?? 0;
  const hasImages = (industryReach?.imageUrls?.length || 0) > 0;

  if (!isAiOnboarding && !reach) return null;

  if (isAiOnboarding && reach === 0) {
    return (
      <p className="u-half-spacing-bottom">
        Looks good! We’ve filled in the details—take a moment to review and adjust anything you’d like as you prepare
        your profile to connect with 100+ investors, startups, and corporates in your space
      </p>
    );
  }

  return (
    <TextAvatarGroup className="u-half-spacing-bottom">
      <span className="u-quarter-spacing-right">
        {isAiOnboarding
          ? 'Looks good! We’ve filled in the details — please take a moment to review and adjust anything you’d like as you prepare your profile to connect with'
          : 'Nice! Preview and prepare your profile to connect with'}
      </span>
      {hasImages && (
        <AvatarGroup max={5} size={30}>
          {industryReach!.imageUrls.map(url => (
            <Avatar key={url} imageUrl={url} borderColor="grey" />
          ))}
        </AvatarGroup>
      )}
      {reach > 0 && (
        <span>
          {hasImages ? ' and ' : ''}
          {reach} {hasImages ? 'other' : ''} investors, early stage companies and corporates within your industries
        </span>
      )}
    </TextAvatarGroup>
  );
}

export default function BasicCompanyProfileForm({
  onComplete,
  onBack,
  onClose,
  companyProfile,
  setCompanyProfile,
}: {
  onComplete: () => void;
  onBack: () => void;
  onClose: () => void;
  companyProfile: CompanyProfile;
  setCompanyProfile: (companyProfile: CompanyProfile) => void;
}) {
  const notify = useNotify();
  const { setLastUpdated } = useCompanyLastUpdated(companyProfile.id);

  const { resource: fundingStageOptionsResource } = useFundingStages();
  const fundingStageOptions =
    getOrUndefined(fundingStageOptionsResource)?.values?.map(s => ({ ...s, value: s.id })) ?? [];

  const companies = useCompanies();

  const [updatedProfile, setUpdatedProfile] = useState<CompanyProfile>(companyProfile);

  const [updateCompany, isSaving] = useLazyResource(
    () =>
      companiesApi.update(updatedProfile.id, {
        location: updatedProfile.location,
        established: updatedProfile.established,
        employees: updatedProfile.employees,
        website: updatedProfile.website,
        mission: updatedProfile.mission,
        fundingStage: updatedProfile.fundingStage?.id,
        companyRegistrationNumber: updatedProfile.companyRegistrationNumber || null,
      }),
    {
      onSuccess: () => {
        setCompanyProfile(updatedProfile);
        onComplete();
      },
      onFailure: () => notify('error', 'Failed to save company profile'),
    },
  );

  const { resource: industryReachResource } = useResource<CompanyIndustryReach>(
    companyProfile.industries.values.length > 0
      ? companiesAPIUrls.insights.industryReach(companyProfile.id)
      : undefined,
  );

  const industryReach = getOrElse(industryReachResource, { reach: { lowerLimit: 0 }, imageUrls: [] });

  const width = useWindowWidth();
  const dictionaryColumnCount =
    width < bluePlanetTheme.breakpoints.values.sm ? 1 : width < bluePlanetTheme.breakpoints.values.md ? 2 : 4;

  const dictInputWidth = width < bluePlanetTheme.breakpoints.values.md ? '100%' : '260px';
  const aiRef = useRef<HTMLDivElement>(null);

  const { isMobile } = useBreakpoints();
  const [isAiOnboarding] = useAiOnboardingSessionStorage();

  const [showAiFeedback, setShowAiFeedback] = useState(isAiOnboarding);
  const [submitFeedback] = useLazyResource((feedback: Feedback) => {
    setShowAiFeedback(false);
    return companiesApi.onboarding.postFeedback(companyProfile.slug, { feedback });
  });
  return (
    <>
      <Content className="u-flex u-flex--column u-flex-align-center">
        <BasicProfileMessage industryReach={industryReach} />
        <div style={{ width: '100%' }}>
          {isAiOnboarding && <AIChip className="u-half-spacing-bottom" />}
          <Resource resource={companies.resource}>
            {companyList => (
              <>
                <ImageUpload
                  width={0}
                  imageUrl={updatedProfile.imageURL}
                  aspectRatio={BANNER_IMAGE_ASPECT_RATIO}
                  onUploadSuccess={async image => {
                    setUpdatedProfile({ ...updatedProfile, imageURL: image.secure_url });
                    await companiesApi.update(companyProfile.id, { imageURL: image.secure_url });
                    const updatedCompany = { ...updatedProfile, imageURL: image.secure_url };
                    invalidate(companyProfileKey(companyProfile.slug), updatedCompany);
                    invalidate(companyProfileByIdKey(companyProfile.id), updatedCompany);
                    setLastUpdated(new Date());
                  }}
                  onUploadFailure={() => {
                    notify('error', 'Upload failed. Only jpeg & png images are allowed.');
                  }}
                  getUploadUrl={filename => companiesApi.images.getCompanyUploadUrl(companyProfile.id, filename)}
                  placeholder={(openDropzone, height) => (
                    <CtaButton
                      onClick={openDropzone}
                      style={{ borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }}
                      height={height}
                    >
                      Add a cover image that represents your brand
                    </CtaButton>
                  )}
                />
                <LogoHeader className="u-flex u-content-spacing-top text-h4">
                  <ImageUpload
                    width={80}
                    aspectRatio={1}
                    onUploadSuccess={async image => {
                      setUpdatedProfile({ ...updatedProfile, logoURL: image.secure_url });
                      await companiesApi.update(companyProfile.id, { logoURL: image.secure_url });
                      const updatedCompany = { ...updatedProfile, logoURL: image.secure_url };
                      invalidate(companyProfileKey(companyProfile.slug), updatedCompany);
                      invalidate(companyProfileByIdKey(companyProfile.id), updatedCompany);
                      invalidate<List<CompanyListItem>>(companiesKey, {
                        ...companyList,
                        values: companyList.values.map(v =>
                          v.id === updatedProfile.id ? { ...v, logoURL: image.secure_url } : v,
                        ),
                      });
                      invalidateCommunities();
                      setLastUpdated(new Date());
                    }}
                    onUploadFailure={() => {
                      notify('error', 'Upload failed. Only jpeg & png images are allowed.');
                    }}
                    getUploadUrl={filename => companiesApi.images.getCompanyUploadUrl(companyProfile.id, filename)}
                    imageUrl={resize(updatedProfile.logoURL, { width: 500, height: 500 })}
                    shape="round"
                    placeholder={(openDropzone, height) => (
                      <CtaButton onClick={openDropzone} height={height} shape="circle">
                        Add logo
                      </CtaButton>
                    )}
                  />
                  {companyProfile.name}
                </LogoHeader>
              </>
            )}
          </Resource>
          <HorizontalDictionary columnCount={dictionaryColumnCount} className="u-content-spacing-top">
            {[
              {
                key: 'Est',
                value: (
                  <InlineEditableField
                    label="Add established"
                    defaultValue={updatedProfile.established ?? ''}
                    onConfirm={value => setUpdatedProfile({ ...updatedProfile, established: value })}
                    renderInputComponent={(onChange, value) => (
                      <TextField
                        autoFocus
                        placeholder='e.g. "2015"'
                        type="number"
                        value={value}
                        onChange={e => {
                          onChange(e.target.value);
                        }}
                      />
                    )}
                    valueComponent={<>{updatedProfile.established}</>}
                  />
                ),
              },
              {
                key: 'Location',
                value: (
                  <InlineEditableField
                    label="Add location"
                    defaultValue={updatedProfile.location}
                    onConfirm={value => setUpdatedProfile({ ...updatedProfile, location: value })}
                    inputWrapperStyle={{ width: dictInputWidth }}
                    autoSaveOnChange={true}
                    renderInputComponent={onChange => (
                      <LocationInput
                        style={{ width: '100%' }}
                        name="location"
                        searchArea="places"
                        placeholder="Search for location"
                        autoComplete="address-level2"
                        autoFocus
                        onChange={v => onChange(v || undefined)}
                      />
                    )}
                    valueComponent={<>{updatedProfile.location && formatShortLocation(updatedProfile.location)}</>}
                  />
                ),
              },
              {
                key: 'Funding stage',
                value: (
                  <InlineEditableField
                    label="Add funding stage"
                    defaultValue={updatedProfile.fundingStage}
                    onConfirm={fundingStage => {
                      if (fundingStage) {
                        setUpdatedProfile({ ...updatedProfile, fundingStage });
                      }
                    }}
                    renderInputComponent={(onChange, value) => (
                      <Select
                        style={{ width: '100%' }}
                        autoFocus
                        name="fundingStage"
                        value={value}
                        placeholder="Select current funding stage"
                        getOptionLabel={i => i.name}
                        options={fundingStageOptions}
                        onChange={onChange}
                      />
                    )}
                    valueComponent={<>{updatedProfile.fundingStage?.name ?? '-'}</>}
                  />
                ),
              },
              {
                key: 'Employees',
                value: (
                  <InlineEditableField
                    label="Add employees"
                    defaultValue={updatedProfile.employees ? `${updatedProfile.employees}` : ''}
                    buttonStyle={{ height: 25 }}
                    onConfirm={value => {
                      const employees = parseInt(value, 10);
                      if (!isNaN(employees)) {
                        setUpdatedProfile({ ...updatedProfile, employees });
                      } else {
                        setUpdatedProfile({ ...updatedProfile, employees: undefined });
                      }
                    }}
                    renderInputComponent={(onChange, value) => (
                      <TextField
                        autoFocus
                        type="number"
                        value={value}
                        onChange={e => {
                          onChange(e.target.value);
                        }}
                      />
                    )}
                    valueComponent={<>{updatedProfile.employees}</>}
                  />
                ),
              },
            ]}
          </HorizontalDictionary>
          <div className="u-content-spacing-top">
            <div style={{ display: 'inline-block' }} className="u-quarter-spacing-right">
              <InlineEditableField
                buttonStyle={{ minWidth: '140px' }}
                label="Add industries"
                defaultValue={updatedProfile.industries.values}
                onConfirm={value => {
                  setUpdatedProfile({ ...updatedProfile, industries: { values: value } });
                  CompanyAPI.industries.update(companyProfile.id, value);
                }}
                renderInputComponent={(onChange, value) => (
                  <IndustryPicker
                    autoFocus
                    style={{ minWidth: 240 }}
                    selectedIndustries={value}
                    setIndustries={onChange}
                  />
                )}
                valueComponent={
                  <ChipList>
                    <TruncatedList
                      values={updatedProfile.industries.values}
                      limit={3}
                      renderItem={label => <IndustryChip label={label.name} />}
                      renderExpandButton={(label, onClick) => <IndustryChip onClick={onClick} label={label.name} />}
                    />
                  </ChipList>
                }
              />
            </div>
            <div style={{ display: 'inline-block', minWidth: '140px' }}>
              <InlineEditableField
                label="Add stage"
                defaultValue={updatedProfile.stages.values}
                onConfirm={value => {
                  setUpdatedProfile({ ...updatedProfile, stages: { values: value } });
                  CompanyAPI.stages.update(companyProfile.id, value);
                }}
                renderInputComponent={(onChange, value) => (
                  <StagePicker
                    autoFocus
                    productStage={value[0]}
                    style={{ minWidth: '200px' }}
                    industries={updatedProfile.industries.values}
                    onChange={selectedValue => onChange(selectedValue ? [selectedValue] : [])}
                  />
                )}
                valueComponent={
                  <>
                    {updatedProfile.stages.values.map(stage => (
                      <StageChip key={stage.name} label={stage.name} />
                    ))}
                  </>
                }
              />
            </div>
          </div>
          <div className="u-content-spacing-top">
            <InlineEditableField
              label="Add your mission statement"
              defaultValue={updatedProfile.mission || ''}
              buttonStyle={{ height: '100px' }}
              onConfirm={value => setUpdatedProfile({ ...updatedProfile, mission: value })}
              disableEnterKey
              renderInputComponent={(onChange, value) => (
                <div className="u-flex u-flex--column u-flex-grow">
                  <TextField
                    inputProps={{ style: { lineHeight: '25px' } }}
                    autoFocus
                    maxLength={MISSION_MAX_LENGTH}
                    multiline
                    minRows={4}
                    maxRows={20}
                    suffixPosition="start-bottom"
                    value={value}
                    suffix={
                      <AiMenu
                        companyId={companyProfile.id}
                        organizationId={undefined}
                        input={value || ''}
                        maxLength={700}
                        onReplace={v => {
                          onChange(v);
                        }}
                        aiRef={aiRef}
                      />
                    }
                    onChange={e => {
                      onChange(e.target.value);
                    }}
                  />
                  <div ref={aiRef} />
                </div>
              )}
              valueComponent={
                <div style={{ minHeight: '100px' }} className="text-body">
                  {updatedProfile.mission}
                </div>
              }
            />
          </div>
          <div className="u-content-spacing-top">
            <h3 className="text-subheading-2">Company registration number</h3>
            <InlineEditableField
              label="Add company registration number"
              explanation="Your official company registration or organisation number"
              defaultValue={updatedProfile.companyRegistrationNumber ?? ''}
              onConfirm={value => setUpdatedProfile({ ...updatedProfile, companyRegistrationNumber: value })}
              renderInputComponent={(onChange, value) => (
                <TextField
                  autoFocus
                  maxLength={COMPANY_REGISTRATION_NUMBER_MAX_LENGTH}
                  placeholder="e.g. 912 345 678"
                  value={value}
                  onChange={e => {
                    onChange(e.target.value);
                  }}
                />
              )}
              valueComponent={
                <DictionaryValue className="text-body">{updatedProfile.companyRegistrationNumber}</DictionaryValue>
              }
            />
          </div>
        </div>
      </Content>
      <DialogActions align="space-between">
        <ButtonList>
          <Button isLoading={isSaving} onClick={updateCompany} kind="primary">
            Next
          </Button>
          <Button onClick={onBack} kind="primary" color="greyLight">
            Back
          </Button>
        </ButtonList>
        <Button onClick={onClose} kind="tertiary" color="grey">
          Pick it up later
        </Button>

        {showAiFeedback && (
          <Snackbar
            sx={{
              bottom: isMobile ? 90 : undefined,
              '.MuiPaper-root': {
                backgroundColor: bluePlanetTheme.bluePlanetPalette.white,
                color: bluePlanetTheme.bluePlanetPalette.indigo.main,
              },
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            open
            message="Did our AI get this right?"
            color=""
            action={
              <>
                <div className="u-flex u-flex-center u-flex--gap-half">
                  <IconButton size="small" onClick={() => submitFeedback('Positive')}>
                    <ThumbsUpIcon />
                  </IconButton>
                  <IconButton size="small" onClick={() => submitFeedback('Negative')}>
                    <ThumbsDownIcon />
                  </IconButton>
                </div>
              </>
            }
          />
        )}
      </DialogActions>
    </>
  );
}
